import { useEffect } from 'react';
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useWindowSize } from '@scramble/hooks';
import { TopHeader } from '@/components/TopHeader';
import { useGetMe } from '@/hooks/shared/useGetMe';
import { APP_LINKS } from '@/helpers/links';
import { useUserStore } from '@/store/userStore';
import { ChatwootChat } from '@/chat/chatwoot';
import { useGetChatwootIdentity } from '@/query/queries/chatwoot/useGetChatwootIdentity';
import { Navigation } from '@/components/Navigation';
import { TabletNavigation } from '@/components/TabletNavigation';
import { Footer } from '@/components/Footer';
import { CabinetGuard } from '@/components/CabinetGuard';
import './index.scss';

export const AppLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { profile } = useGetMe();
  const accessToken = useUserStore(state => state.accessToken);
  const { innerWidth } = useWindowSize();

  const { data: chatwootIdentity } = useGetChatwootIdentity({
    inbox: 'founder',
    enabled: !!accessToken && import.meta.env.VITE_CHAT_CHATWOOT === 'on',
  });

  useEffect(() => {
    if (location.pathname === APP_LINKS.root && accessToken) {
      navigate(APP_LINKS.dashboard, { replace: true });
    }
  }, [accessToken, location.pathname, navigate]);

  useEffect(() => {
    if (!chatwootIdentity || !profile?.user) return;

    const user = profile.user;
    const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ');

    ChatwootChat.setUser({
      identifier: chatwootIdentity.identifier,
      identifierHash: chatwootIdentity.identifier_hash,
      email: user.email,
      name: fullName,
      phoneNumber: user.phone,
      avatarUrl: user.photo?.file,
      countryCode: user.country_code,
      companyName: profile.company?.name,
    });
    ChatwootChat.setCustomAttributes({
      scramble_user_id: user.id,
      role: user.role,
      status: user.status,
      country: user.country,
      citizenship: user.citizenship,
      citizenship_code: user.citizenship_code,
    });
  }, [chatwootIdentity, profile]);

  if (!accessToken) {
    return <Navigate to={APP_LINKS.signIn} replace />;
  }

  return (
    <div className="root_page">
      {innerWidth > 1380 ? <Navigation /> : <TabletNavigation />}
      <main id="main">
        <div className="root_page__content">
          <TopHeader />
          <CabinetGuard>
            <Outlet />
          </CabinetGuard>
        </div>
        {innerWidth >= 1380 && <Footer />}
      </main>
      {innerWidth < 1380 && <Footer />}
    </div>
  );
};